import type { ItemModifiers } from './item';
import type { CharacterClassId, CharacterRaceId } from './character';
import type { SpellSounds } from '../data/sounds';

// ============================================================================
// SPELL ENUMS
// ============================================================================

export type SpellCastType = 'target' | 'self' | 'area' | 'screen' | 'toggle';

export type SpellTargetType = 'self' | 'players' | 'npcs' | 'players_and_npcs' | 'ground';

export type SpellDamageType = 'physical' | 'magical' | 'pure';

export type SpellResourceType = 'mana' | 'stamina' | 'hp';

export type SpellCostMode = 'flat' | 'percent' | 'per_second';

export type SpellDurationType = 'fixed' | 'permanent' | 'until_death' | 'until_kill_player' | 'until_kill_npc';

export type SpellStackBehavior = 'stack' | 'refresh' | 'override';

export type SpellAuraTarget = 'self' | 'allies' | 'enemies' | 'all';

/**
 * Afiliación de un objetivo candidato RESPECTO al caster. Un objetivo se clasifica en
 * exactamente UNA categoría (mutuamente excluyentes) vía `classifyAffiliation()` en
 * `shared/utils/spellAffiliation.ts`. El orden de declaración es el orden de PRIORIDAD
 * (self primero) y se usa para desempatar / futuro auto-targeting.
 *
 * Cada spell declara en `affects[]` a qué relaciones puede pegar. Así un buff sólo cura
 * aliados y un daño sólo toca enemigos, sin importar el `castType`/`targetType`.
 */
export enum SpellAffiliation {
  /** El propio caster. */
  SELF = 'self',
  /** Criatura invocada por el caster. FUTURO: requiere `ownerId` en la entidad summon. */
  OWN_SUMMON = 'own_summon',
  /** Criatura domada por el caster. FUTURO: requiere `masterId` en la criatura domada. */
  OWN_TAMED = 'own_tamed',
  /** Player con la misma facción (!= null) que el caster. El clan es un subconjunto de la facción. */
  ALLY_PLAYER = 'ally_player',
  /** NPC no atacable (sacerdote/vendor/mentor): `attackable === false`. */
  NEUTRAL_NPC = 'neutral_npc',
  /** NPC/mob atacable: `attackable === true`. */
  HOSTILE_NPC = 'hostile_npc',
  /** Player que no es self ni aliado (facción distinta o sin facción). */
  ENEMY_PLAYER = 'enemy_player',
}

export type SpellTag =
  | 'offensive' | 'defensive' | 'support' | 'debuff' | 'aura'
  | 'summon' | 'heal' | 'dot' | 'crowd_control' | 'movement'
  | 'pvp' | 'pve' | 'hybrid' | 'high_risk' | 'toggle';

// ============================================================================
// SPELL RESOURCE COST
// ============================================================================

export interface SpellResourceCost {
  resource: SpellResourceType;
  amount: number;
  mode: SpellCostMode;
}

// ============================================================================
// SPELL EFFECTS
// ============================================================================

export interface SpellEffectDamage {
  type: 'damage';
  damageType: SpellDamageType;
  minDamage: number;
  maxDamage: number;
  /** Scaling factor from caster's magic damage (0.0-2.0+) */
  scalingFactor?: number;
  /**
   * Sweet-spot por DISTANCIA (artillería): el daño escala con qué tan lejos está el
   * objetivo. A distancia `fullFromTiles` o más → 100%; pegado (dist 0) → `minMultiplier`
   * (lineal entre medio). Premia castear desde lejos y castiga el corto alcance.
   */
  rangeFalloff?: { fullFromTiles: number; minMultiplier: number };
}

export interface SpellEffectHeal {
  type: 'heal';
  minHeal: number;
  maxHeal: number;
  scalingFactor?: number;
}

export interface SpellEffectManaRestore {
  type: 'mana_restore';
  amount: number;
  isPercent?: boolean;
}

export interface SpellEffectManaDrain {
  type: 'mana_drain';
  amount: number;
  isPercent?: boolean;
}

/**
 * Hooks de FX del ciclo de vida de un buff/debuff (todos opcionales — ver
 * docs/PARTICLES.md §FX de buffs). Cada uno apunta al catálogo de partículas
 * (`fx_*`). Un buff no necesita definir todos; es una base para ir vistiendo.
 *  - `applyFxId`  → one-shot sobre CADA objetivo al aplicarse (client, `onSpellCastResult`).
 *  - `activeFxId` → persistente sobre el objetivo MIENTRAS dura (canal `auraFxIds`).
 *  - `expireFxId` → one-shot sobre el objetivo al IRSE (client, cuando `activeFxId` sale
 *    de `auraFxIds`). Requiere `activeFxId` (es su gatillo).
 */
export interface SpellBuffFx {
  applyFxId?: string;
  activeFxId?: string;
  expireFxId?: string;
}

export interface SpellEffectBuff extends SpellBuffFx {
  type: 'buff';
  modifiers: ItemModifiers;
  duration_ms: number;
  durationType: SpellDurationType;
  stackBehavior: SpellStackBehavior;
  maxStacks?: number;
  /** Unique buff ID prefix (final ID = `spell_{spellId}_{targetId}`) */
  buffIdPrefix: string;
}

export interface SpellEffectDebuff extends SpellBuffFx {
  type: 'debuff';
  modifiers: ItemModifiers;
  duration_ms: number;
  durationType: SpellDurationType;
  stackBehavior: SpellStackBehavior;
  maxStacks?: number;
  buffIdPrefix: string;
}

export interface SpellEffectStatusEffect {
  type: 'status_effect';
  statusType:
    | 'poison'
    | 'burn'
    | 'freeze'
    | 'silence'
    | 'slow'
    | 'bleed'
    | 'blind'
    | 'immobilize'
    | 'paralyze';
  duration_ms: number;
  tickInterval_ms?: number;
  damagePerTick?: number;
  damageType?: SpellDamageType;
  modifiers?: ItemModifiers;
  stackBehavior: SpellStackBehavior;
}

export interface SpellEffectTeleport {
  type: 'teleport';
}

/**
 * Blink a la espalda: teletransporta al caster al tile DETRÁS del objetivo (el lado
 * opuesto al que el objetivo mira) y lo deja mirando en la misma dirección que el
 * objetivo (viéndole la espalda). Requiere target activo (castType 'target'). El
 * destino se computa server-side desde el target y se valida con la TeleportPolicy
 * existente (rango + tile libre). Base de hechizos de reposicionamiento tipo asesino.
 * Ver docs/SPELLS.md.
 */
export interface SpellEffectBlinkBehind {
  type: 'blink_behind';
}

/**
 * Desplazamiento genérico: mueve al CASTER o al OBJETIVO a un tile computado respecto
 * a la posición/heading del par caster↔target. Requiere target activo. Se valida con la
 * misma TeleportPolicy que el blink (tile libre); si no hay lugar, se detiene en el
 * último tile válido del camino (movimiento parcial). Base de cargas, jalones y empujones.
 *   - subject: quién se mueve ('self' = caster, 'target' = objetivo).
 *   - mode:
 *       'front_of_target'  → al tile FRENTE del objetivo (carga/gap-closer; self).
 *       'toward_caster'    → acerca al sujeto hacia el caster (jalón; target).
 *       'away_from_caster' → aleja al sujeto del caster (empujón/knockback; target).
 *   - tiles: cuántos tiles avanzar (para toward/away; front_of_target = 1).
 * Ver docs/SPELLS.md y docs/COMBAT_SYSTEMS.md.
 */
export interface SpellEffectDisplace {
  type: 'displace';
  subject: 'self' | 'target';
  mode: 'front_of_target' | 'toward_caster' | 'away_from_caster';
  tiles: number;
}

/**
 * Devuelve la movilidad al objetivo: limpia inmovilizar Y paralizar (clearImpair).
 * Usado por los hechizos de liberación (target aliado / área support / self). Dispara
 * el FX one-shot de movilidad restaurada en cada objetivo afectado.
 */
export interface SpellEffectRemoveImpair {
  type: 'remove_impair';
}

export interface SpellEffectResurrect {
  type: 'resurrect';
  /** Fracción de maxHp con la que despierta el objetivo (0.0-1.0). Ej: 0.30 = 30%. */
  healPercent: number;
}

/**
 * Provocación (taunt): fuerza a las entidades ENEMIGAS dentro de `radius` tiles
 * (Chebyshev) del caster a caminar hacia él y pegarle melee por `durationMs`. Lo
 * aplica GameRoom (escaneo por radio), no el dispatch por-target. Ver docs/TAUNT.md.
 */
export interface SpellEffectTaunt {
  type: 'taunt';
  /** Radio en tiles alrededor del caster (1 = los 8 tiles adyacentes). */
  radius: number;
  /** Duración de la provocación en ms. */
  durationMs: number;
}

/**
 * Control mental de invocación: toma el control de la invocación ENEMIGA apuntada por
 * `durationMs` ms (0 = permanente). La aplica GameRoom sobre el target resuelto. Brujo =
 * temporal, Monje = permanente. Ver docs/SUMMONS.md.
 */
export interface SpellEffectControlSummon {
  type: 'control_summon';
  durationMs: number;
}

/**
 * Desvincular: disipa la invocación PROPIA apuntada (target OWN_SUMMON). La aplica
 * SummonController.unsummonOwn sobre el target resuelto, validando el ownership. No pasa
 * por el switch de executeSpell (se post-procesa en SpellCastHandlers, igual que
 * control_summon). Ver docs/SUMMONS.md.
 */
export interface SpellEffectUnsummon {
  type: 'unsummon';
}

/**
 * Vuelve invisible al objetivo (normalmente `affects: [SELF]`) por `duration_ms`.
 * Si el objetivo tiene el mod único `invisibilityNeverExpires`, la invisibilidad
 * queda permanente. Se rompe al iniciar un ataque. Ver docs/INVISIBILITY.md.
 */
export interface SpellEffectInvisibility {
  type: 'invisibility';
  duration_ms: number;
  /**
   * Si true, la invisibilidad es PERMANENTE (sigilo del asesino): no expira por tiempo
   * (equivale a tener `invisibilityNeverExpires`). Se rompe igual al atacar. `duration_ms`
   * se ignora. Ver docs/INVISIBILITY.md.
   */
  permanent?: boolean;
}

/**
 * Detección: revela (globalmente, para todos los clientes) a los objetivos
 * invisibles resueltos por `castType` (screen/area) durante `duration_ms`. No corta
 * su timer de invisibilidad, sólo los hace visibles por la ventana. Ver docs/INVISIBILITY.md.
 */
export interface SpellEffectRevealInvisibility {
  type: 'reveal_invisibility';
  duration_ms: number;
}

export interface SpellEffectSummon {
  type: 'summon';
  /**
   * Perfil de summon (catálogo `shared/data/summonProfiles.ts`) que define TODO
   * el comportamiento de la invocación. Cuando está seteado, el SummonSystem lo
   * usa como fuente de verdad y los campos de abajo quedan como metadata/legacy.
   */
  profileId?: string;
  npcTemplateId: string;
  count: number;
  duration_ms: number;
  maxActiveSummons: number;
  behavior: 'follow_and_attack';
}

export type SpellEffect =
  | SpellEffectDamage
  | SpellEffectHeal
  | SpellEffectManaRestore
  | SpellEffectManaDrain
  | SpellEffectBuff
  | SpellEffectDebuff
  | SpellEffectStatusEffect
  | SpellEffectTeleport
  | SpellEffectBlinkBehind
  | SpellEffectDisplace
  | SpellEffectRemoveImpair
  | SpellEffectResurrect
  | SpellEffectTaunt
  | SpellEffectControlSummon
  | SpellEffectUnsummon
  | SpellEffectInvisibility
  | SpellEffectRevealInvisibility
  | SpellEffectSummon;

// ============================================================================
// AURA DEFINITION
// ============================================================================

export interface SpellAuraDefinition {
  radiusTiles: number;
  /**
   * @deprecated El gate amigo/enemigo ahora vive en `SpellDefinition.affects`
   * (clasificación por afiliación). Se conserva sólo por retrocompatibilidad de datos;
   * `processAuraTicks` ya no lo lee. Ver `SpellAffiliation`.
   */
  affectsTarget?: SpellAuraTarget;
  tickInterval_ms: number;
  particleEffectId?: string;
  /** Mana reservation as percentage (0.0-1.0) of maxMana */
  manaReservePercent?: number;
}

// ============================================================================
// LEARNING REQUIREMENTS
// ============================================================================

export interface SpellLearningRequirements {
  minLevel?: number;
  /**
   * Whitelist de clases que pueden aprender este spell.
   * Si está seteada, sólo estas clases pueden aprenderlo (forbiddenClasses se ignora).
   */
  allowedClasses?: CharacterClassId[];
  /**
   * Blacklist de clases que NO pueden aprender este spell.
   * Se aplica solo si allowedClasses NO está seteada. Si ninguna está → spell global.
   */
  forbiddenClasses?: CharacterClassId[];
  /**
   * Whitelist de razas que pueden aprender este spell.
   * Si está seteada, sólo estas razas pueden aprenderlo (forbiddenRaces se ignora).
   */
  allowedRaces?: CharacterRaceId[];
  /**
   * Blacklist de razas que NO pueden aprender este spell.
   * Se aplica solo si allowedRaces NO está seteada.
   */
  forbiddenRaces?: CharacterRaceId[];
  skillTreeNodes?: string[];
}

// ============================================================================
// MAIN SPELL DEFINITION
// ============================================================================

export interface SpellDefinition {
  id: string;
  name: string;
  nameEs: string;
  description: string;
  descriptionEs: string;
  icon: string;

  /**
   * Si true, este hechizo NO se auto-aprende en el login: sólo se obtiene mientras un
   * item con `grantsSpellId` lo otorga (equipado). Al desequipar el item se olvida y
   * se saca de la hotbar. Pensado para hechizos "temporales" atados a equipamiento.
   * Ver docs/SPELLS.md y `Item.grantsSpellId`.
   */
  grantOnly?: boolean;

  // Casting
  castType: SpellCastType;
  targetType: SpellTargetType;
  /** Rango MÁXIMO en tiles (0 = sin tope, salvo el view-range del AOI). 0 para self-cast. */
  range: number;
  /**
   * Rango MÍNIMO en tiles (zona muerta): no podés castear a un objetivo más cerca
   * que esto. `0`/undefined = sin mínimo. Lo enforcea SpellSystem.checkSpellRange
   * (Chebyshev caster→objetivo). Pensado para hechizos de artillería/AoE que no
   * podés dejar caer encima tuyo. Ver docs/SPELLS.md.
   */
  minRange?: number;
  /** ZxZ area size for area spells */
  areaSize?: number;
  /** Cast time in seconds (0 = instant) */
  castTime: number;
  interruptible: boolean;
  /** Animation ID to play on cast complete */
  animationId?: number;
  /** Animation ID to loop during cast time (visible to all) */
  castAnimationId?: number;
  /** Spell words shown above character in cyan (unless noSpellWords) */
  spellWords?: string;
  /** Words shown via normal chat system (white, like player speech) */
  chatWords?: string;

  // Cooldown in seconds
  cooldown: number;

  /**
   * Si true, este hechizo COMPARTE el cooldown del golpe melee (`attack`): no se puede
   * castear mientras el golpe melee esté en cooldown, y al castearlo también pone el
   * golpe melee en cooldown. Evita encadenar golpe melee → hechizo (o viceversa) sin
   * intervalo para meter dos "golpes" seguidos. Pensado para hechizos-golpe tipo asesino
   * (blink + strike). Lo enforcea GameRoom.handleCastSpellMessage. Ver docs/SPELLS.md.
   */
  sharesMeleeCooldown?: boolean;

  /**
   * Subtipos de arma requeridos para castear (WeaponSubtype como string). Si está
   * seteado, sólo se puede castear con un arma de esos subtipos equipada — habilita builds
   * "limitadas a un tipo de arma". Lo enforcea SpellCastHandlers.handleCastSpellMessage.
   * `undefined`/`[]` = sin restricción de arma. Ver docs/SPELLS.md.
   */
  requiredWeaponSubtypes?: string[];

  // Resource costs (can have multiple)
  costs: SpellResourceCost[];

  // Effects applied in order
  effects: SpellEffect[];

  /**
   * Whitelist de afiliaciones (relativas al caster) a las que este spell puede pegar.
   * Filtro server-authoritative en `resolveTargets` / `processAuraTicks`: un candidato sólo
   * recibe los effects si `classifyAffiliation(caster, target) ∈ affects`.
   *
   * Ejemplos:
   *  - Buff aliado:      `[SELF, ALLY_PLAYER]`
   *  - Daño ofensivo:    `[HOSTILE_NPC, ENEMY_PLAYER]`
   *  - Buff a summons:   `[OWN_SUMMON]` (válido hoy, nunca matchea hasta que existan summons)
   *
   * Define A QUIÉN afecta; `castType`/`targetType` definen CÓMO se juntan los candidatos.
   */
  affects: SpellAffiliation[];

  /**
   * FX disparado en cada target dañado por este spell (tanto en cast normal
   * como en ticks de aura). Apunta al catálogo de partículas (formato `fx_*`).
   * El cliente lo dispara desde `onSpellCastResult` por cada target con damage>0.
   */
  onHitFxId?: string;

  /**
   * FX one-shot disparado sobre el CASTER cuando el cast resuelve (client-side,
   * desde `onSpellCastResult`). Momento "casteo" del ciclo de FX. Apunta al
   * catálogo `fx_*`. Útil para el fogonazo del que lanza un buff sobre sí o aliados.
   * Ver docs/PARTICLES.md §FX de buffs.
   */
  castFxId?: string;

  // Aura config (only for toggle spells)
  aura?: SpellAuraDefinition;

  // Max active instances (for auras/summons)
  maxActiveInstances: number;

  // Learning requirements
  requirements: SpellLearningRequirements;

  // Tags for filtering, UI, and internal logic
  tags: SpellTag[];

  /**
   * Sonidos opcionales del hechizo (inicio/loop/fin de casteo, impacto, kills). La MAYORÍA de los
   * hechizos NO define ninguno: caen al comportamiento por defecto (ninguno o el default de spells).
   * Cada campo apunta a un id de `SOUNDS` (shared/data/sounds.ts). Ver docs/SOUND_AND_MUSIC.md.
   */
  sounds?: SpellSounds;
}
